import { useState } from "react";
import { Link } from "wouter";
import { Calendar, MapPin, Camera, Tag } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Event } from "@/types/schema";

interface EventCardProps {
    event: Event;
    showGalleryButton?: boolean;
    className?: string;
}

const getStatusBadge = (status: string) => {
    const statusConfig = {
        "PendingApproval": { label: "Pendente", variant: "outline" as const },
        "Approved": { label: "Aprovado", variant: "default" as const },
        "Rejected": { label: "Rejeitado", variant: "destructive" as const },
        "Closed": { label: "Encerrado", variant: "outline" as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig];

    // Não mostrar badge para Draft ou status não reconhecidos
    if (!config) {
        return null;
    }

    return <Badge variant={config.variant}>{config.label}</Badge>;
};

export default function EventCard({
    event,
    showGalleryButton = true,
    className = ""
}: EventCardProps) {
    const [imageError, setImageError] = useState(false);

    return (
        <Link to={`/event/${event.id}`} className="block h-full">
            <Card className={`overflow-hidden border-0 shadow-md hover:shadow-2xl transition-all duration-300 group transform hover:-translate-y-2 bg-white rounded-lg cursor-pointer h-full flex flex-col ${className}`}>
                <div className="h-32 relative overflow-hidden flex-shrink-0">
                    {(event as any).coverimage && !imageError ? (
                        <img
                            src={(event as any).coverimage}
                            alt={event.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 filter group-hover:brightness-110"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary via-teal-500 to-teal-600 flex items-center justify-center group-hover:from-teal-600 group-hover:to-primary transition-all duration-500">
                            <Camera className="h-16 w-16 text-white opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                        </div>
                    )}
                    <div className="absolute top-3 right-3 opacity-90 group-hover:opacity-100 transition-opacity duration-300">
                        {getStatusBadge(event.status)}
                    </div>
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </div>

                <CardContent className="p-5 group-hover:bg-gray-50/50 transition-colors duration-300 flex-1 flex flex-col">
                    <div className="mb-4 flex-shrink-0">
                        <h3 className="font-bold text-base line-clamp-2 mb-2 text-gray-900 group-hover:text-primary transition-colors duration-300">
                            {event.title}
                        </h3>
                        <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                            {event.description || `Reviva os melhores momentos do ${event.title}. Encontre suas fotos!`}
                        </p>
                    </div>

                    <div className="space-y-2 mb-4 flex-1">
                        {event.tags && (
                            <div className="flex items-center gap-2 text-xs text-gray-500 group-hover:text-gray-700 transition-colors duration-300">
                                <Tag className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                                <span className="line-clamp-1 font-medium">{event.tags}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-500 group-hover:text-gray-700 transition-colors duration-300">
                            <Calendar className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                            <span className="font-medium">{new Date(event.date).toLocaleDateString('pt-BR')}</span>
                        </div>
                        {event.location && (
                            <div className="flex items-center gap-2 text-xs text-gray-500 group-hover:text-gray-700 transition-colors duration-300">
                                <MapPin className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                                <span className="line-clamp-1 font-medium">{event.location}</span>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2 flex-shrink-0">
                        <Link to={`/event/${event.id}`} className="flex-1">
                            <button
                                className="w-full text-xs font-semibold border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 group-hover:shadow-md transform group-hover:scale-105 px-3 py-2 rounded-md"
                            >
                                Ver Evento
                            </button>
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}
